import { Dia } from 'modelos/src';

/**
 * Conversión del cronograma de las configs de evento (`dias` / `horaInicio` /
 * `horaFin`) entre la hora local del usuario y UTC.
 *
 * El backend guarda y compara en UTC: `dias` son los días UTC en que EMPIEZA
 * la ventana y las horas son "HH:mm" UTC. Si `horaInicio > horaFin` la ventana
 * cruza la medianoche y termina al día siguiente. Al correr la ventana se
 * corre su inicio (y con él, el día) y la duración se conserva.
 *
 * Usa el offset actual del navegador: para zonas con horario de verano, una
 * ventana guardada en invierno queda corrida una hora en verano.
 */

const DIAS: Dia[] = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miercoles',
  'Jueves',
  'Viernes',
  'Sabado',
];

/** Orden de presentación: de lunes a domingo. */
const ORDEN: Dia[] = [...DIAS.slice(1), DIAS[0]];

const MIN_DIA = 24 * 60;

export interface ICronogramaHorario {
  dias?: Dia[] | null;
  horaInicio?: string | null;
  horaFin?: string | null;
}

/** Acepta "HH:mm" y también "HHmm": el formulario viejo no validaba el
 *  patrón y en la base hay horas como "1400". */
function aMinutos(hhmm?: string | null): number | null {
  const m = /^(\d{1,2}):?(\d{2})$/.exec(hhmm?.trim() ?? '');
  if (!m || +m[1] > 23 || +m[2] > 59) return null;
  return +m[1] * 60 + +m[2];
}

function aHHMM(minutos: number): string {
  const m = ((minutos % MIN_DIA) + MIN_DIA) % MIN_DIA;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Minutos que hay que sumarle a la hora local para obtener UTC. */
function offsetLocalAUtc(): number {
  return new Date().getTimezoneOffset();
}

function correr<T extends ICronogramaHorario>(c: T, delta: number): T {
  const ini = aMinutos(c.horaInicio);
  const fin = aMinutos(c.horaFin);
  if (ini === null || fin === null) return c;
  const iniCorrido = ini + delta;
  const corrimientoDias = Math.floor(iniCorrido / MIN_DIA);
  const dias = c.dias
    ?.map((d) => DIAS[(((DIAS.indexOf(d) + corrimientoDias) % 7) + 7) % 7])
    .sort((a, b) => ORDEN.indexOf(a) - ORDEN.indexOf(b));
  return {
    ...c,
    dias,
    horaInicio: aHHMM(iniCorrido),
    horaFin: aHHMM(fin + delta),
  };
}

/** Hora local del usuario → UTC, para guardar. */
export function cronogramaLocalAUtc<T extends ICronogramaHorario>(c: T): T {
  return correr(c, offsetLocalAUtc());
}

/** UTC (como viene del backend) → hora local del usuario, para mostrar o editar. */
export function cronogramaUtcALocal<T extends ICronogramaHorario>(c: T): T {
  return correr(c, -offsetLocalAUtc());
}
